/**
 * Converts 24-hour format time string to 12-hour format with AM/PM
 * @param time24h - Time string in 24-hour format (e.g., "14:30")
 * @returns Time string in 12-hour format (e.g., "PM 2:30")
 */
export const convertTo12HourFormat = (time24h: string): string => {
    if (!time24h?.trim() || typeof time24h !== 'string') {
        return time24h || '';
    }

    const timeParts = time24h.split(':');
    if (timeParts.length < 2 || timeParts.length > 3) {
        return time24h;
    }

    const [hoursStr, minutesStr = '00'] = timeParts;
    const hour24 = parseInt(hoursStr, 10);

    if (isNaN(hour24) || hour24 < 0 || hour24 > 23) {
        return time24h;
    }

    const period = hour24 < 12 ? 'AM' : 'PM';
    const hour12 = hour24 % 12 || 12;

    const formattedMinute = minutesStr.padStart(2, '0');

    return `${period} ${hour12}:${formattedMinute}`;
};

/** Formats the kiosk device's local time as AM/PM HH:mm. */
export const formatLocalClockTime = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return convertTo12HourFormat(`${hours}:${minutes}`);
};

/**
 * Formats operating hours from 24-hour format to 12-hour format range
 * @param openTime - Opening time in 24-hour format (e.g., "09:00")
 * @param closeTime - Closing time in 24-hour format (e.g., "21:00")
 * @returns Formatted time range (e.g., "AM 9:00~PM 9:00")
 */
export const formatOperatingHours = (openTime: string, closeTime: string): string => {
    if (!openTime?.trim() || !closeTime?.trim()) {
        return '';
    }

    const formattedOpenTime = convertTo12HourFormat(openTime);
    const formattedCloseTime = convertTo12HourFormat(closeTime);

    if (formattedOpenTime === openTime || formattedCloseTime === closeTime) {
        return '';
    }

    return `${formattedOpenTime}~${formattedCloseTime}`;
};

/**
 * Formats operating hours in simple format without AM/PM
 * @param openTime - Opening time in 24-hour format (e.g., "09:00")
 * @param closeTime - Closing time in 24-hour format (e.g., "17:00")
 * @returns Formatted time range (e.g., "9:00~17:00")
 */
export const formatOperatingHoursSimple = (openTime: string, closeTime: string): string => {
    if (!openTime?.trim() || !closeTime?.trim()) {
        return '';
    }

    const formatTime = (time: string): string => {
        const [hoursStr, minutesStr = '00'] = time.split(':');
        const hour = parseInt(hoursStr, 10);
        const minute = minutesStr.padStart(2, '0');

        if (isNaN(hour) || hour < 0 || hour > 23) {
            return time;
        }

        return `${hour}:${minute}`;
    };

    const formattedOpenTime = formatTime(openTime);
    const formattedCloseTime = formatTime(closeTime);

    return `${formattedOpenTime}~${formattedCloseTime}`;
};

/**
 * Parses 24-hour format time string for TTS usage
 * @param time24h - Time string in 24-hour format (e.g., "14:30")
 * @returns Object with hour and minute in 12-hour format
 */
export const parseTimeForTTS = (time24h: string) => {
    if (!time24h?.trim()) return { hour: '', minute: '' };

    const [hoursStr, minutesStr = '00'] = time24h.split(':');
    const hour24 = parseInt(hoursStr, 10);
    const minute = parseInt(minutesStr, 10);

    if (isNaN(hour24) || hour24 < 0 || hour24 > 23) {
        return { hour: '', minute: '' };
    }

    const hour12 = hour24 % 12 || 12;

    return {
        hour: hour12.toString(),
        minute: minute.toString()
    };
};

/**
 * Converts hour number to Korean TTS format
 * @param hour - Hour number (1-12)
 * @returns Korean word for the hour
 */
const convertHourToKorean = (hour: number): string => {
    const koreanHours: { [key: number]: string } = {
        1: '한',
        2: '두',
        3: '세',
        4: '네',
        5: '다섯',
        6: '여섯',
        7: '일곱',
        8: '여덟',
        9: '아홉',
        10: '열',
        11: '열한',
        12: '열두'
    };

    return koreanHours[hour] || hour.toString();
};

/**
 * Gets activity time TTS parameters from open and close times
 * @param openTime - Opening time in 24-hour format (e.g., "09:00")
 * @param closeTime - Closing time in 24-hour format (e.g., "21:00")
 * @param language - Current language (optional, defaults to 'ko')
 * @returns Object with TTS template variables and minute availability info
 */
export const getActivityTimeTTS = (openTime: string, closeTime: string, language: string = 'ko') => {
    const open = parseTimeForTTS(openTime);
    const close = parseTimeForTTS(closeTime);

    const hasOpenMinute = open.minute && open.minute !== '0';
    const hasCloseMinute = close.minute && close.minute !== '0';

    // Only convert to Korean for Korean language
    const openTimeFormatted = language === 'ko' ? convertHourToKorean(parseInt(open.hour)) : open.hour;
    const closeTimeFormatted = language === 'ko' ? convertHourToKorean(parseInt(close.hour)) : close.hour;

    return {
        openTime: openTimeFormatted,
        openMinute: open.minute,
        closeTime: closeTimeFormatted,
        closeMinute: close.minute,
        hasOpenMinute,
        hasCloseMinute
    };
};
